import { colord, extend } from 'colord';
import mixPlugin from 'colord/plugins/mix';
import namesPlugin from 'colord/plugins/names';

import { deepMerge } from '../utils/object';
import logger from '../logger';

extend([mixPlugin, namesPlugin, whiteBlackMixPlugin]);

/**
 *
 * Custom colord plugin
 *
 * plugin depends on 'colord/plugins/mix' plugin
 *
 * @param {class} ColordClass - The colord class, that gets extended
 *
 */
function whiteBlackMixPlugin(ColordClass) {
    // pre-parsing colors into colord instances, to save some milliseconds
    const blackC = colord('#000');
    const whiteC = colord('#FFF');

    /**
     *
     * Using mix with white instead of lighten, as lighten does not have the same visual effect
     * see codepen https://codepen.io/emdeoh/pen/zYOQOPB
     *
     * @param {number} amount - value between 0 and 1
     * @return {Object}
     *
     */
    ColordClass.prototype.mixWhite = function (amount) {
        return this.mix(whiteC, 1 - amount);
    };

    /**
     *
     * Using mix with white instead of darken, as darken does not have the same visual effect
     * see codepen https://codepen.io/emdeoh/pen/zYOQOPB
     *
     * @param {number} amount - value between 0 and 1
     * @return {Object}
     *
     */
    ColordClass.prototype.mixBlack = function (amount) {
        return this.mix(blackC, 1 - amount);
    };
}

/**
 *
 * Generate a new theme based upon the colour variables
 *
 * @param {Object} baseTheme
 * @param {string} highlightColor any valid css colour
 * @param {string} navigationColor any valid css colour
 * @return {Object} a new theme object
 *
 */
function makeTheme(baseTheme, highlightColor, navigationColor) {
    console.log("Make theme - highlightColor is:" + highlightColor);
    const baseThemeColor = baseTheme.color;

    const highlightColorVariants = makeColorVariants(
        highlightColor,
        baseThemeColor.highlightColor
    );

    const navigationColorVariants = makeColorVariants(
        navigationColor,
        baseThemeColor.navigationColor
    );

    const color = {
        highlightColor,
        navigationColor,
        highlightColorVariants,
        navigationColorVariants,
    };

    // use deep merge to ensure to keep any existing values that have no overrides
    return deepMerge(baseTheme, {
        color,
    });
}

/**
 *
 * Generate the required variants of the given colour
 *
 * @param {string} color any valid css colour
 * @param {string?} fallbackColor any valid css colour
 * @return {ColorVariants} variants based upon the color
 *
 */
function makeColorVariants(color, fallbackColor) {
    let parsedColor = colord(color);

    if (!parsedColor.isValid()) {
        logger.warn(`Invalid color ${color} passed, using fallback`);
        parsedColor = colord(fallbackColor);
    }

    /**
     *
     * @typedef {Object} ColorVariants
     * @property {string} w15 mix with white `15%`
     * @property {string} w25 mix with white `25%`
     * @property {string} b15 mix with black `15%`
     * @property {string} b25 mix with black `25%`
     *
     */
    return {
        w15: parsedColor.mixWhite(0.15).toRgbString(),
        w25: parsedColor.mixWhite(0.25).toRgbString(),
        //
        b15: parsedColor.mixBlack(0.15).toRgbString(),
        b25: parsedColor.mixBlack(0.25).toRgbString(),
    };
}

export { makeTheme, makeColorVariants };
